import React from "react";
import { BotStatus, Guild } from "../services/api";

interface DashboardProps {
  botStatus: BotStatus | null;
  guilds: Guild[];
  loading: boolean;
  onNavigate?: (tab: string) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({
  botStatus,
  guilds,
  loading,
}) => {
  const formatUptime = (uptime: number | undefined) => {
    if (!uptime) return "0h 0m";
    const days = Math.floor(uptime / (1000 * 60 * 60 * 24));
    const hours = Math.floor((uptime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((uptime % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return <div className="loading">Caricamento dashboard in corso...</div>;
  }

  const totalMembers = guilds.reduce((acc, g) => acc + (g.memberCount || 0), 0);

  return (
    <div className="tab-content dashboard-view">
      <div className="dashboard-hero">
        <div className="hero-text">
          <h2>Panoramica Generale Bot</h2>
          <p>Monitora lo stato operativo, server connessi e carico di VortexBot</p>
        </div>
        <div className={`status-badge-hero ${botStatus?.online ? "online" : "offline"}`}>
          <span className="pulse-dot"></span>
          <span>{botStatus?.online ? "SISTEMI OPERATIVI" : "OFFLINE"}</span>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon-wrapper blue">🌐</div>
          <div className="stat-content">
            <div className="stat-value">{botStatus?.guilds || guilds.length}</div>
            <div className="stat-label">Server Connessi</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper green">👥</div>
          <div className="stat-content">
            <div className="stat-value">{botStatus?.users || totalMembers}</div>
            <div className="stat-label">Utenti Raggiunti</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper purple">⏱️</div>
          <div className="stat-content">
            <div className="stat-value">{formatUptime(botStatus?.uptime)}</div>
            <div className="stat-label">Tempo di Attività (Uptime)</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper gold">📶</div>
          <div className="stat-content">
            <div className="stat-value">{botStatus?.ping ? `${botStatus.ping} ms` : "0 ms"}</div>
            <div className="stat-label">Latenza WebSocket</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper teal">💬</div>
          <div className="stat-content">
            <div className="stat-value">{botStatus?.channels || 0}</div>
            <div className="stat-label">Canali Discord</div>
          </div>
        </div>
      </div>

      {/* Guilds List */}
      <div className="card">
        <div className="card-header-row">
          <div>
            <h3>Server Attivi ({guilds.length})</h3>
            <p className="card-desc">Tutti i server Discord in cui il bot è presente con permessi</p>
          </div>
        </div>

        {guilds.length === 0 ? (
          <div className="empty-state">
            <p>Il bot non è attualmente presente in alcun server.</p>
          </div>
        ) : (
          <div className="guilds-grid">
            {guilds.map((guild) => (
              <div key={guild.id} className="guild-card">
                <img
                  src={guild.icon || "/default-guild.png"}
                  alt={guild.name}
                  className="guild-icon"
                  onError={(e: any) => {
                    e.target.src = "https://cdn.discordapp.com/embed/avatars/0.png";
                  }}
                />
                <div className="guild-info">
                  <h4>{guild.name}</h4>
                  <div className="guild-meta">
                    <span className="members-tag">👥 {guild.memberCount} membri</span>
                    <span className="guild-id">ID: {guild.id}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
